/*
 * HOW LONG IS LEFT, AND WHETHER TO SAY ANYTHING AT ALL.
 *
 * Pure, and separate from the strip that draws it, because the interesting
 * part is not the markup. It is the four ways this can lie to somebody about
 * their own account:
 *
 * It can tell a paying contractor he is on a trial, which happens the moment
 * an "unknown" state is treated as a trial while the access view is still
 * being read.
 *
 * It can invent a number, which happens the moment a missing end date is
 * filled in with a default of seven.
 *
 * It can round four hours up to a day, which is how a man decides to renew
 * tomorrow morning and finds the account closed when he gets there.
 *
 * And it can keep counting after the clock has run out.
 *
 * All four are decided here, where they can be tested, rather than inside a
 * component where they can only be looked at.
 */

export type TrialSaid = { text: string; cta: string }

const HOUR = 3_600_000
const DAY = 86_400_000

/**
 * How much of a trial is left, in one place.
 *
 * The strip at the top and the billing screen both say a number of days, and
 * two of these written separately is how a contractor reads five in one place
 * and four in the other on the same afternoon. Days round UP, because the part
 * day is still a day he can work in; the last day is reported in hours instead,
 * because "1 day left" on a trial with four hours in it is what makes a man
 * decide to renew tomorrow morning.
 */
export function trialLeft(
  trialEndsAt: string | null,
  now: number,
): { ms: number; days: number; hours: number } | null {
  if (!trialEndsAt) return null
  const end = Date.parse(trialEndsAt)
  if (!Number.isFinite(end)) return null
  const ms = end - now
  return { ms, days: Math.max(0, Math.ceil(ms / DAY)), hours: Math.max(0, Math.floor(ms / HOUR)) }
}

/**
 * What the trial strip should say, or null when there is nothing honest to
 * say - which covers paid accounts, an access state that has not come back
 * yet, and a trial with no end date on it.
 *
 * AND ONE ACCOUNT IS NEVER TOLD ABOUT ITSELF TWICE.
 *
 * An expired trial is both a lapsed pass and an ended trial, so the app was
 * stacking two notices about the same fact: an amber bar saying the pass has
 * ended over a green strip saying the trial has ended, in two different tones,
 * about one thing, with two different ways out. On a phone that is 130 pixels
 * of chrome before the app starts. The bar wins, because it is the one that
 * says what still works and carries the way to fix it. Passing allowed in is
 * how that rule lands here, where it can be tested, rather than in the strip
 * where it can only be looked at.
 */
export function trialWording(
  access: string,
  trialEndsAt: string | null,
  now: number,
  allowed = true,
): TrialSaid | null {
  if (!allowed) return null
  if (access === 'expired') return { text: 'Your trial has ended.', cta: 'See plans' }

  if (access !== 'trial') return null
  if (!trialEndsAt) return null

  const left = trialLeft(trialEndsAt, now)
  if (!left) return null
  if (left.ms <= 0) return { text: 'Your trial has ended.', cta: 'See plans' }

  if (left.ms < DAY) {
    if (left.hours < 1) return { text: 'Your trial ends within the hour.', cta: 'Upgrade' }
    return {
      text: `${left.hours} ${left.hours === 1 ? 'hour' : 'hours'} left in your trial`,
      cta: 'Upgrade',
    }
  }

  const days = left.days
  return { text: `${days} ${days === 1 ? 'day' : 'days'} left in your trial`, cta: 'Upgrade' }
}
